import React, { memo, useCallback, useContext, useMemo, useRef, useState } from 'react';

import {
    Advice,
    Checkbox,
    createUseStyles,
    Icon,
    Popover,
    Select,
    Text,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { cleanString } from '@Sage/ssm-utils';
import classnames from 'classnames';
import Fuse from 'fuse.js';
import PropTypes from 'prop-types';

import { useWeb5Context } from 'lib/Web5Context';

import styles from '../styles/MultiEnvironmentSection';
import MenuItem from './MenuItem';
import { SidebarContext } from './SidebarContext';
const useStyles = createUseStyles(styles, 'MultiEnvironmentSection');

const fuseOptions = {
    threshold: 0.2,
    keys: ['label'],
    getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path);
        return cleanString(value);
    },
};

const MultiEnvironmentSection = memo(
    ({
        currentHref,
        defaultEnvironment,
        environments = [],
        environmentsSelected = [],
        items = [],
        onEnvironmentsChange,
        onMatchActive,
        onMouseEnter,
        onMouseLeave,
    }) => {
        const { t } = useWeb5Context();
        const classes = useStyles();
        const theme = useTheme();
        const { isExpanded } = useContext(SidebarContext);
        const [isPopoverOpen, setIsPopoverOpen] = useState(false);
        const [searchText, setSearchText] = useState('');
        const [showMinSelectionWarning, setShowMinSelectionWarning] = useState(false);
        const clickedElementRef = useRef(null);

        const environmentsText = useMemo(() => {
            if (environmentsSelected.length === 0) return '';
            if (environmentsSelected.length === 1) {
                return environmentsSelected[0]?.label?.toUpperCase() || '';
            }
            const firstEnv = environmentsSelected[0]?.label?.toUpperCase() || '';
            const remaining = environmentsSelected.length - 1;
            return `${firstEnv}, +${remaining}`;
        }, [environmentsSelected]);

        const environmentsCount = useMemo(() => {
            return environmentsSelected.length;
        }, [environmentsSelected.length]);

        const handleTogglePopover = useCallback((newState) => {
            setIsPopoverOpen(newState);
            if (!newState) {
                setSearchText('');
                setShowMinSelectionWarning(false);
            }
        }, []);

        const handleSearchChange = useCallback(({ inputValue, action, setNewInputValue }) => {
            if (action?.action === 'input-change') {
                const newValue = inputValue || '';
                setSearchText(newValue);
                setNewInputValue(newValue);
            }
        }, []);

        const handleSearchClear = useCallback((value, action) => {
            if (action?.action === 'clear') {
                setSearchText('');
            }
        }, []);

        const fuseInstance = useMemo(() => new Fuse(environments, fuseOptions), [environments]);

        const filteredEnvironments = useMemo(() => {
            if (!searchText) return environments;
            const finalSearch = cleanString(searchText.toLowerCase());
            return fuseInstance.search(finalSearch).map((result) => result.item);
        }, [environments, fuseInstance, searchText]);

        const selectedSet = useMemo(
            () => new Set(environmentsSelected.map((env) => env.value)),
            [environmentsSelected],
        );

        const allSelected = useMemo(() => {
            if (filteredEnvironments.length === 0) return false;
            return filteredEnvironments.every((env) => selectedSet.has(env.value));
        }, [filteredEnvironments, selectedSet]);

        const handleSelectAll = useCallback(() => {
            if (!onEnvironmentsChange) return;

            const filteredIdsSet = new Set(filteredEnvironments.map((env) => env.value));

            if (allSelected) {
                const remainingSelected = environmentsSelected.filter(
                    (env) => !filteredIdsSet.has(env.value),
                );
                // If deselecting all would leave 0 selected, keep the default environment
                const defaultEnv = environments.find((env) => env.value === defaultEnvironment);
                const finalSelection =
                    remainingSelected.length === 0 && defaultEnv ? [defaultEnv] : remainingSelected;
                onEnvironmentsChange(finalSelection);

                if (remainingSelected.length === 0) {
                    setShowMinSelectionWarning(true);
                }
            } else {
                // User is adding environments, hide warning
                setShowMinSelectionWarning(false);
                const alreadySelected = environmentsSelected.filter(
                    (env) => !filteredIdsSet.has(env.value),
                );
                const newSelection = [...alreadySelected, ...filteredEnvironments];
                onEnvironmentsChange(newSelection);
            }
        }, [
            allSelected,
            defaultEnvironment,
            environments,
            environmentsSelected,
            filteredEnvironments,
            onEnvironmentsChange,
        ]);

        const handleToggleEnvironment = useCallback(
            (env) => {
                if (!onEnvironmentsChange) return;

                const isSelected = environmentsSelected.some(
                    (selected) => selected.value === env.value,
                );

                if (isSelected) {
                    const newSelected = environmentsSelected.filter(
                        (selected) => selected.value !== env.value,
                    );
                    onEnvironmentsChange(newSelected);
                } else {
                    // User is adding an environment, hide warning
                    setShowMinSelectionWarning(false);
                    onEnvironmentsChange([...environmentsSelected, env]);
                }
            },
            [environmentsSelected, onEnvironmentsChange],
        );

        const isEnvironmentSelected = useCallback(
            (env) => selectedSet.has(env.value),
            [selectedSet],
        );

        const isOnlyOneSelected = useMemo(
            () => environmentsSelected.length === 1,
            [environmentsSelected.length],
        );

        const handleAttemptDeselectLast = useCallback((event) => {
            const clickedElement = event?.currentTarget;
            clickedElementRef.current = clickedElement;
            setShowMinSelectionWarning(true);

            // Scroll to clicked element after the warning appears
            requestAnimationFrame(() => {
                if (clickedElement) {
                    clickedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            });
        }, []);

        const handleDismissWarning = useCallback(() => {
            setShowMinSelectionWarning(false);
        }, []);

        const menuItemsContent = useMemo(() => {
            return items.map((item, index) => {
                const {
                    type: _type,
                    isMultiEnvironment: _isMultiEnvironment,
                    ...otherProps
                } = item;

                const selected = onMatchActive
                    ? onMatchActive(item)
                    : currentHref?.includes(item.link) ||
                      item.link === `..${window.location.pathname}`;

                const itemProps = {
                    isActive: selected || false,
                    onMouseEnter,
                    onMouseLeave,
                    ...otherProps,
                };

                return (
                    <MenuItem
                        key={`multi-env-item-${item.link || index}`}
                        className={classes.menuItem}
                        {...itemProps}
                    />
                );
            });
        }, [classes.menuItem, currentHref, items, onMatchActive, onMouseEnter, onMouseLeave]);

        const selectProps = useMemo(
            () => ({
                className: classes.searchInput,
                customOnChangeInput: handleSearchChange,
                inputValue: searchText,
                isFuzzy: true,
                isFullWidth: true,
                keepInputValueOnBlur: true,
                keepValueOnInputChange: true,
                onChange: handleSearchClear,
                placeholder: t('label_search'),
                useAsSimpleSearch: true,
            }),
            [classes.searchInput, handleSearchChange, handleSearchClear, searchText, t],
        );

        const popoverContent = useMemo(() => {
            return (
                <div className={classes.popoverContentWrapper}>
                    <Select {...selectProps} />
                    <div className={classes.environmentsList}>
                        {filteredEnvironments.length > 0 ? (
                            <>
                                <div
                                    className={classnames(
                                        classes.environmentItem,
                                        classes.selectAllItem,
                                    )}
                                    onClick={handleSelectAll}
                                >
                                    <Checkbox checked={allSelected} readOnly />
                                    <Text className={classes.environmentLabel}>
                                        {t('label_selectall')}
                                    </Text>
                                </div>
                                {filteredEnvironments.map((env) => {
                                    const isSelected = isEnvironmentSelected(env);
                                    const isDisabled = isOnlyOneSelected && isSelected;
                                    return (
                                        <div
                                            key={env.value}
                                            className={classnames(
                                                classes.environmentItem,
                                                classes.environmentItemIndented,
                                                {
                                                    [classes.environmentItemDisabled]: isDisabled,
                                                },
                                            )}
                                            onClick={(e) =>
                                                isDisabled
                                                    ? handleAttemptDeselectLast(e)
                                                    : handleToggleEnvironment(env)
                                            }
                                        >
                                            <Checkbox checked={isSelected} readOnly />
                                            <Text className={classes.environmentLabel}>
                                                {env.label}
                                            </Text>
                                        </div>
                                    );
                                })}
                            </>
                        ) : (
                            <Text
                                className={classes.noResultsText}
                                color={theme.colors.primary.white}
                            >
                                {t('label_search_no_results')}
                            </Text>
                        )}
                    </div>
                    {showMinSelectionWarning && (
                        <div className={classes.adviceContainer}>
                            <Advice
                                type="info"
                                showIcon
                                showCollapse={false}
                                className={classes.advice}
                            >
                                {t('label_multi_environment_menu_at_least_one_option_selected')}
                            </Advice>
                            <Icon
                                name="close"
                                className={classes.adviceCloseIcon}
                                onClick={handleDismissWarning}
                            />
                        </div>
                    )}
                </div>
            );
        }, [
            allSelected,
            classes,
            filteredEnvironments,
            handleAttemptDeselectLast,
            handleDismissWarning,
            handleSelectAll,
            handleToggleEnvironment,
            isEnvironmentSelected,
            isOnlyOneSelected,
            selectProps,
            showMinSelectionWarning,
            t,
            theme.colors.primary.white,
        ]);

        return (
            <div
                className={classnames(classes.container, {
                    [classes.isExpanded]: isExpanded,
                })}
            >
                <Popover
                    content={popoverContent}
                    placement="rightTop"
                    visible={isPopoverOpen}
                    onVisibleChange={handleTogglePopover}
                    className={classes.popoverContainer}
                >
                    <div
                        className={classnames(classes.environmentSelector, {
                            [classes.environmentSelectorActive]: isPopoverOpen,
                        })}
                    >
                        <div className={classes.environmentSelectorContainer}>
                            {isExpanded ? (
                                <>
                                    {environmentsText && (
                                        <Text
                                            color={theme.colors.primary.white}
                                            medium
                                            useTooltip
                                            isTruncated
                                        >
                                            {environmentsText}
                                        </Text>
                                    )}
                                    <Text
                                        className={classes.environmentSelectorText}
                                        color={theme.colors.primary.brilliantGreen}
                                        type="badges"
                                    >
                                        {t('label_environment')}
                                    </Text>
                                </>
                            ) : (
                                <Text
                                    color={theme.colors.primary.white}
                                    className={classes.environmentCount}
                                    medium
                                    useTooltip
                                    isTruncated
                                >
                                    {environmentsCount}
                                </Text>
                            )}
                        </div>
                        <Icon
                            name="play"
                            color={theme.colors.primary.brilliantGreen}
                            size="small"
                        />
                    </div>
                </Popover>
                <div className={classes.itemsList}>{menuItemsContent}</div>
            </div>
        );
    },
);

MultiEnvironmentSection.propTypes = {
    currentHref: PropTypes.string,
    defaultEnvironment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    environments: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
    environmentsSelected: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
    ),
    items: PropTypes.array,
    onEnvironmentsChange: PropTypes.func,
    onMatchActive: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
};

export default MultiEnvironmentSection;
